import { BrokeragePostResponse } from '@lio/api-types';
import { brokerageKeys } from '@services/brokerage';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

// The single source of truth for this union -- BrokerageHierarchyDialog derives both its Select
// options and its submit-time cast from this instead of spelling the 4 values out independently.
export const BROKERAGE_ORGANIZATIONAL_TYPES = ['Headquarters', 'Division', 'Office', 'Other'] as const;
export type BrokerageOrganizationalType = (typeof BROKERAGE_ORGANIZATIONAL_TYPES)[number];

export const useUpdateBrokerage = (brokerageId: string | undefined) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const mutationFn = ({
    osProducerUsername,
    entityStatus,
    restrictQuoteAccessToProducer,
    parentId,
    taxReportingBrokerageId,
    commissionPayeeBrokerageId,
    organizationalType,
  }: {
    osProducerUsername?: string;
    entityStatus?: string;
    restrictQuoteAccessToProducer?: boolean;
    parentId?: string | null;
    taxReportingBrokerageId?: string | null;
    commissionPayeeBrokerageId?: string | null;
    organizationalType?: BrokerageOrganizationalType | null;
  }) =>
    client
      .patch(`brokerage/${brokerageId}`, {
        json: {
          osProducerUsername,
          entityStatus,
          restrictQuoteAccessToProducer,
          parentId,
          taxReportingBrokerageId,
          commissionPayeeBrokerageId,
          organizationalType,
        },
      })
      .json<BrokeragePostResponse>();

  const result = useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...brokerageKeys.brokerage, { brokerageId }] });
      queryClient.invalidateQueries({ queryKey: brokerageKeys.search });
    },
  });

  return { ...result, updateBrokerage: result.mutateAsync };
};
